/**
 * 主题状态管理 Store
 *
 * 管理系统主题配色方案，支持三种主题：
 * - default: 蒲公英黄（默认）
 * - professional-blue: 专业蓝（适合正式医疗环境）
 * - eye-care-green: 护眼绿（适合长时间阅读病历）
 *
 * @module stores/themeStore
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import {
  generateElementPlusOverrideStyle,
  injectThemeStyle,
} from "../../src/utils/theme-injector";

/** 主题类型定义 */
export type ThemeName = "professional-blue" | "eye-care-green" | "warm-light";

/** 主题配置接口 */
export interface ThemeConfig {
  name: ThemeName;
  label: string;
  description: string;
  preview: {
    primary: string;
    background: string;
    accent: string;
  };
}

/** 可用主题列表 */
export const AVAILABLE_THEMES: ThemeConfig[] = [
  {
    name: "professional-blue",
    label: "专业蓝",
    description: "低饱和度灰蓝，专业冷静，适合正式医疗环境（默认）",
    preview: {
      primary: "#4A90A4",
      background: "#F5F7F9",
      accent: "#5B8FB9",
    },
  },
  {
    name: "eye-care-green",
    label: "护眼绿",
    description: "低饱和度灰绿，减少蓝光刺激，适合长时间阅读",
    preview: {
      primary: "#7A9E7E",
      background: "#F5F8F5",
      accent: "#6B8FA0",
    },
  },
  {
    name: "warm-light",
    label: "温馨暖色",
    description: "柔和暖橙色，温馨亲切，适合儿科/妇科",
    preview: {
      primary: "#E6A23C",
      background: "#FDFBF7",
      accent: "#F3D19E",
    },
  },
];

/** localStorage 存储键 */
const THEME_STORAGE_KEY = "pgy-mdss-theme";

/**
 * 颜色混合函数 (Hex)
 * 安全地混合两个颜色
 */
const mixColor = (color1: string, color2: string, weight: number) => {
  weight = Math.max(Math.min(Number(weight), 1), 0);

  // 简单的格式检查
  if (!color1 || !color1.startsWith("#") || color1.length < 7) {
    console.warn(`Invalid color1 for mixColor: ${color1}`);
    return color1 || "#000000";
  }
  if (!color2 || !color2.startsWith("#") || color2.length < 7) {
    console.warn(`Invalid color2 for mixColor: ${color2}`);
    return color2 || "#ffffff";
  }

  const r1 = parseInt(color1.substring(1, 3), 16);
  const g1 = parseInt(color1.substring(3, 5), 16);
  const b1 = parseInt(color1.substring(5, 7), 16);
  const r2 = parseInt(color2.substring(1, 3), 16);
  const g2 = parseInt(color2.substring(3, 5), 16);
  const b2 = parseInt(color2.substring(5, 7), 16);
  const r = Math.round(r1 * (1 - weight) + r2 * weight);
  const g = Math.round(g1 * (1 - weight) + g2 * weight);
  const b = Math.round(b1 * (1 - weight) + b2 * weight);
  const _r = ("0" + (r || 0).toString(16)).slice(-2);
  const _g = ("0" + (g || 0).toString(16)).slice(-2);
  const _b = ("0" + (b || 0).toString(16)).slice(-2);
  return "#" + _r + _g + _b;
};

/**
 * 主题管理 Store
 */
export const useThemeStore = defineStore("theme", () => {
  // ==================== State ====================

  /** 当前主题名称 */
  const currentTheme = ref<ThemeName>(loadThemeFromStorage());

  /** 主题是否正在切换中 */
  const isTransitioning = ref(false);

  // ==================== Getters ====================

  /** 获取当前主题配置 */
  const currentThemeConfig = () => {
    return (
      AVAILABLE_THEMES.find((t) => t.name === currentTheme.value) ||
      AVAILABLE_THEMES[0]
    );
  };

  // ==================== Actions ====================

  /**
   * 切换主题
   * @param themeName 主题名称
   */
  function setTheme(themeName: ThemeName) {
    // 即使主题相同也强制重新应用，防止样式丢失
    isTransitioning.value = true;
    currentTheme.value = themeName;

    // 保存到 localStorage
    saveThemeToStorage(themeName);

    // 应用主题 CSS 变量
    try {
      applyTheme(themeName);
    } catch (e) {
      console.error("❌ 应用主题失败:", e);
    }

    // 过渡动画结束后重置状态
    setTimeout(() => {
      isTransitioning.value = false;
    }, 300);
  }

  /**
   * 初始化主题（应用启动时调用）
   */
  function initTheme() {
    applyTheme(currentTheme.value);
  }

  /**
   * 重置为默认主题
   */
  function resetTheme() {
    setTheme("professional-blue");
  }

  // ==================== Private Functions ====================

  /**
   * 从 localStorage 加载主题
   */
  function loadThemeFromStorage(): ThemeName {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      // 兼容旧的 'default' 主题，映射到 'professional-blue'
      if (stored === "default") {
        return "professional-blue";
      }
      if (stored && isValidTheme(stored)) {
        return stored as ThemeName;
      }
    } catch (e) {
      console.warn("Failed to load theme from localStorage:", e);
    }
    return "professional-blue"; // 默认使用专业蓝
  }

  /**
   * 保存主题到 localStorage
   */
  function saveThemeToStorage(themeName: ThemeName) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeName);
    } catch (e) {
      console.warn("Failed to save theme to localStorage:", e);
    }
  }

  /**
   * 验证主题名称是否有效
   */
  function isValidTheme(name: string): boolean {
    return AVAILABLE_THEMES.some((t) => t.name === name);
  }

  /**
   * 应用主题 CSS 变量到 document
   */
  function applyTheme(themeName: ThemeName) {
    const root = document.documentElement;
    if (!root) {
      console.error("❌ 无法获取 document.documentElement");
      return;
    }

    // 1. 移除/添加主题类
    root.classList.remove(
      "theme-professional-blue",
      "theme-eye-care-green",
      "theme-warm-light",
    );
    root.classList.add(`theme-${themeName}`);

    // 2. 设置 PGY 系统 CSS 变量
    const themeVars = getThemeVariables(themeName);

    Object.entries(themeVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // 3. 设置 Element Plus CSS 变量
    const primaryColor = themeVars["--pgy-primary"];
    if (primaryColor) {
      updateElementPlusVariables(primaryColor, root);

      // 5. 注入强制覆盖样式
      const overrideStyle = generateElementPlusOverrideStyle(
        primaryColor,
        themeVars["--pgy-medical-green"],
        themeVars["--pgy-medical-orange"],
        themeVars["--pgy-medical-red"],
      );
      injectThemeStyle(overrideStyle);
    } else {
      console.warn("⚠️ 未找到 --pgy-primary 变量，无法更新 Element Plus 主色");
    }

    // 4. 映射其他语义色到 Element Plus
    if (themeVars["--pgy-medical-green"]) {
      root.style.setProperty(
        "--el-color-success",
        themeVars["--pgy-medical-green"],
      );
    }
    if (themeVars["--pgy-medical-orange"]) {
      root.style.setProperty(
        "--el-color-warning",
        themeVars["--pgy-medical-orange"],
      );
    }
    if (themeVars["--pgy-medical-red"]) {
      root.style.setProperty(
        "--el-color-danger",
        themeVars["--pgy-medical-red"],
      );
    }
  }

  /**
   * 更新 Element Plus 主题变量
   */
  function updateElementPlusVariables(primaryColor: string, root: HTMLElement) {
    try {
      root.style.setProperty("--el-color-primary", primaryColor);
      // 生成混色：light-3 (混合白色 30%), light-5, light-7, light-8, light-9
      // 注意：Element Plus 的 light-x 是混合白色 x0%
      root.style.setProperty(
        "--el-color-primary-light-3",
        mixColor(primaryColor, "#ffffff", 0.3),
      );
      root.style.setProperty(
        "--el-color-primary-light-5",
        mixColor(primaryColor, "#ffffff", 0.5),
      );
      root.style.setProperty(
        "--el-color-primary-light-7",
        mixColor(primaryColor, "#ffffff", 0.7),
      );
      root.style.setProperty(
        "--el-color-primary-light-8",
        mixColor(primaryColor, "#ffffff", 0.8),
      );
      root.style.setProperty(
        "--el-color-primary-light-9",
        mixColor(primaryColor, "#ffffff", 0.9),
      );
      // 生成深色：dark-2 (混合黑色 20%)
      root.style.setProperty(
        "--el-color-primary-dark-2",
        mixColor(primaryColor, "#000000", 0.2),
      );
    } catch (e) {
      console.error("❌ 更新 Element Plus 变量失败:", e);
    }
  }

  /**
   * 获取主题 CSS 变量
   */
  function getThemeVariables(themeName: ThemeName): Record<string, string> {
    switch (themeName) {
      case "professional-blue":
        return {
          "--pgy-primary": "#4A90A4",
          "--pgy-primary-light": "#6AAFBF",
          "--pgy-primary-dark": "#3A7A8A",
          "--pgy-primary-hover": "#5BA0B4",
          "--pgy-primary-active": "#3A7A8A",
          "--pgy-gray-50": "#F5F7F9",
          "--pgy-medical-blue": "#5B8FB9",
          "--pgy-medical-green": "#6B9E78",
          "--pgy-medical-orange": "#C4956B",
          "--pgy-medical-red": "#C47070",
        };

      case "eye-care-green":
        return {
          "--pgy-primary": "#7A9E7E",
          "--pgy-primary-light": "#9AB89E",
          "--pgy-primary-dark": "#5A7E5E",
          "--pgy-primary-hover": "#8AAE8E",
          "--pgy-primary-active": "#5A7E5E",
          "--pgy-gray-50": "#F5F8F5",
          "--pgy-medical-blue": "#6B8FA0",
          "--pgy-medical-green": "#6B9E78",
          "--pgy-medical-orange": "#B8A070",
          "--pgy-medical-red": "#B88080",
        };

      case "warm-light":
        return {
          "--pgy-primary": "#E6A23C",
          "--pgy-primary-light": "#F3D19E",
          "--pgy-primary-dark": "#B88230",
          "--pgy-primary-hover": "#EBB563",
          "--pgy-primary-active": "#CF9236",
          "--pgy-gray-50": "#FDFBF7",
          "--pgy-medical-blue": "#5B8FB9",
          "--pgy-medical-green": "#6B9E78",
          "--pgy-medical-orange": "#E6A23C",
          "--pgy-medical-red": "#D67575",
        };

      default:
        return {
          "--pgy-primary": "#4A90A4",
          "--pgy-primary-light": "#6AAFBF",
          "--pgy-primary-dark": "#3A7A8A",
          "--pgy-primary-hover": "#5BA0B4",
          "--pgy-primary-active": "#3A7A8A",
          "--pgy-gray-50": "#F5F7F9",
          "--pgy-medical-blue": "#5B8FB9",
          "--pgy-medical-green": "#6B9E78",
          "--pgy-medical-orange": "#C4956B",
          "--pgy-medical-red": "#C47070",
        };
    }
  }

  return {
    // State
    currentTheme,
    isTransitioning,

    // Getters
    currentThemeConfig,

    // Actions
    setTheme,
    initTheme,
    resetTheme,
  };
});
